"use client";

// src/ui/components/calculator/peptide-picker.tsx
//
// The peptide picker CARD trigger that replaces the old inline dropdown in the
// calculator `pick` slot (handoff 02 §1.3). Full-width card: tinted flask thumb,
// peptide name (700, ellipsis), category (mono), and a right-side "Peptid ändern ▾"
// affordance. Tapping it opens the PickerModal. The LOCKED (spoke) variant keeps a
// read-only treatment with an "Alle Peptide" escape link.

import { useState } from "react";
import { useTranslations } from "next-intl";
import { FlaskConical, ChevronDown } from "lucide-react";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import { PickerModal } from "@/ui/components/calculator/picker-modal";
import { accentClassFor } from "@/ui/components/calculator/cockpit";
import { getPeptideBySlug, getPeptideName, getPeptideCategory } from "@/lib/calculator/peptide-data";
import type { Locale } from "@/config/locale";

/**
 * Soft tint background that pairs with each accent text class. Token-driven (no raw
 * hex): we re-use the same semantic color at 10% so the flask thumb echoes the
 * cockpit accent without hardcoding values.
 */
function thumbTintFor(accentClass: string): string {
	if (accentClass.includes("success")) return "bg-success/10";
	if (accentClass.includes("warning")) return "bg-warning/10";
	return "bg-primary/10";
}

interface PeptidePickerProps {
	selectedSlug: string;
	onChange: (slug: string) => void;
	locale: string;
	/** Spoke pages lock the picker to one peptide (read-only). */
	locked?: boolean;
	/** Authoritative set of slugs actually IN STOCK right now (real Saleor stock). */
	inStockSlugs?: ReadonlySet<string>;
}

export function PeptidePicker({ selectedSlug, onChange, locale, locked, inStockSlugs }: PeptidePickerProps) {
	const t = useTranslations("peptideCalculator");
	const [open, setOpen] = useState(false);
	const loc = locale as Locale;

	const peptide = getPeptideBySlug(selectedSlug);
	const name = peptide ? getPeptideName(peptide, loc) : selectedSlug;
	const category = peptide ? getPeptideCategory(peptide, loc) : "";
	const accentClass = peptide ? accentClassFor(peptide, loc) : "text-primary";
	const tintClass = thumbTintFor(accentClass);

	const thumb = (
		<span
			aria-hidden
			className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] ${tintClass} ${accentClass}`}
		>
			<FlaskConical className="h-5 w-5" />
		</span>
	);

	if (locked) {
		return (
			<div className="flex items-center gap-2.5 rounded-[14px] border-[1.5px] border-border bg-card px-3 py-3 sm:gap-3 sm:px-3.5">
				{thumb}
				<span className="flex min-w-0 flex-1 flex-col">
					<span className="truncate text-[15px] font-bold text-foreground">{name}</span>
					<span className="truncate font-mono text-[11.5px] text-muted-foreground">
						{t("picker.lockedHint", { name })}
					</span>
				</span>
				{/* Escape link: icon-only on the narrowest screens (label via aria-label so it
				    never clips past the card edge), full "Alle Peptide" label from xs up. */}
				<LinkWithChannel
					href="/peptide-calculator"
					aria-label={t("picker.allPeptides")}
					className="inline-flex min-h-9 shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border border-border px-2.5 text-xs font-semibold text-primary transition-colors hover:bg-secondary sm:px-3"
				>
					<span className="hidden xs:inline">{t("picker.allPeptides")}</span>
					<ChevronDown className="h-3.5 w-3.5 -rotate-90" />
				</LinkWithChannel>
			</div>
		);
	}

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-label={t("picker.change")}
				className="hover:border-primary/50 flex min-h-12 w-full items-center gap-2.5 rounded-[14px] border-[1.5px] border-border bg-card px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:gap-3 sm:px-3.5"
			>
				{thumb}
				<span className="flex min-w-0 flex-1 flex-col">
					<span className="truncate text-[15px] font-bold text-foreground">{name}</span>
					<span className="truncate font-mono text-[11.5px] text-muted-foreground">{category}</span>
				</span>
				{/* Change affordance: icon-only on the narrowest screens (the label is carried by
				    the button's aria-label so it never clips past the card edge at 360-430px),
				    full "Peptid ändern" label from sm up. */}
				<span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-muted-foreground">
					<span className="hidden xs:inline">{t("picker.change")}</span>
					<ChevronDown className="h-4 w-4" />
				</span>
			</button>

			<PickerModal
				open={open}
				onClose={() => setOpen(false)}
				onPick={(slug) => {
					onChange(slug);
					setOpen(false);
				}}
				currentSlug={selectedSlug}
				locale={locale}
				inStockSlugs={inStockSlugs}
			/>
		</>
	);
}
